import React, { useEffect, useState } from 'react';
import { AlertTriangle, Info, CheckCircle2, Ban, Wrench } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Banner-type → visual config. One place, no duplication.
 * `emoji` mirrors the PRD spec; lucide icon is rendered alongside for polish.
 */
const BANNER_STYLES = {
  warning:     { container: 'bg-yellow-400 text-black border-yellow-600', Icon: AlertTriangle, emoji: '⚠️' },
  danger:      { container: 'bg-red-600  text-white border-red-800',      Icon: Ban,           emoji: '🚫' },
  info:        { container: 'bg-blue-600 text-white border-blue-800',     Icon: Info,          emoji: 'ℹ️' },
  success:     { container: 'bg-green-600 text-white border-green-800',   Icon: CheckCircle2,  emoji: '✅' },
  maintenance: { container: 'bg-orange-400 text-black border-orange-600', Icon: Wrench,        emoji: '🔧' },
};

/**
 * Pure presentational — used by SiteBanner (live) and by the admin Preview box.
 * Accepts either a full banner object or discrete props.
 */
export function BannerBody({ message_text, banner_type = 'warning' }) {
  const style = BANNER_STYLES[banner_type] || BANNER_STYLES.warning;
  const { Icon } = style;
  return (
    <div
      className={`py-2 px-4 text-center font-bold border-b-4 shadow-lg ${style.container}`}
      data-testid="site-banner-body"
    >
      <div className="flex items-center justify-center gap-2">
        <Icon className="w-5 h-5" />
        <span className="text-sm sm:text-base">
          {style.emoji} {message_text} {style.emoji}
        </span>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  );
}

/**
 * Live banner — fetches from GET /api/admin/site-banner on mount, renders only
 * when is_active=true AND show_on matches this page's `scope`.
 *
 * Usage:
 *   <SiteBanner scope="customer" />   // LandingPage, AuthPage, etc.
 *   <SiteBanner scope="admin" />      // AdminLayout
 */
export default function SiteBanner({ scope = 'customer' }) {
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/site-banner`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setBanner(data);
      } catch {
        // Silent — banner is non-critical. Page must still render.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!banner || !banner.is_active) return null;

  const { show_on, message_text, banner_type } = banner;
  const scopeMatches =
    show_on === 'all' ||
    (show_on === 'customer_only' && scope === 'customer') ||
    (show_on === 'admin_only' && scope === 'admin');

  if (!scopeMatches) return null;

  return (
    <div className="sticky top-0 z-50" data-testid={`site-banner-${scope}`}>
      <BannerBody message_text={message_text} banner_type={banner_type} />
    </div>
  );
}
